import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getInstansiList, createInstansi, updateInstansi, deleteInstansi, getInstansiDetail, createUser } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  ChevronLeft, Plus, Edit, MapPin, Clock, Loader2, Building2, Users,
  Briefcase, UserCheck, UserX, RefreshCw, UserPlus, Eye, EyeOff,
  Phone, Trash2, AlertTriangle, Landmark
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../ui/sonner';

// Jenis Instansi configuration with colors
const JENIS_INSTANSI_CONFIG = {
  'Kantor Dinas': { 
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    cardBorder: 'border-l-indigo-500',
    bgGradient: 'from-indigo-50 to-indigo-100/50'
  },
  'Kantor Kecamatan': { 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    cardBorder: 'border-l-blue-500',
    bgGradient: 'from-blue-50 to-blue-100/50'
  },
  'Kantor Kelurahan/Desa': { 
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    cardBorder: 'border-l-emerald-500',
    bgGradient: 'from-emerald-50 to-emerald-100/50'
  },
  'Puskesmas': { 
    color: 'bg-red-100 text-red-800 border-red-200',
    cardBorder: 'border-l-red-500',
    bgGradient: 'from-red-50 to-red-100/50'
  },
  'Kantor Pos': { 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    cardBorder: 'border-l-orange-500',
    bgGradient: 'from-orange-50 to-orange-100/50'
  },
  'Kantor BUMN/BUMD': { 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    cardBorder: 'border-l-purple-500',
    bgGradient: 'from-purple-50 to-purple-100/50'
  },
  'Kantor Pemerintahan Lainnya': { 
    color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    cardBorder: 'border-l-cyan-500',
    bgGradient: 'from-cyan-50 to-cyan-100/50'
  },
  'Lembaga Swasta': { 
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    cardBorder: 'border-l-amber-500',
    bgGradient: 'from-amber-50 to-amber-100/50'
  }
};

const JENIS_INSTANSI_OPTIONS = [
  'Kantor Dinas',
  'Kantor Kecamatan',
  'Kantor Kelurahan/Desa',
  'Puskesmas',
  'Kantor Pos',
  'Kantor BUMN/BUMD',
  'Kantor Pemerintahan Lainnya',
  'Lembaga Swasta'
];

// Daftar Distrik di Kabupaten Asmat
const DISTRIK_OPTIONS = [
  "Agats", "Atsj", "Sawa Erma", "Akat", "Fayit", "Pantai Kasuari", 
  "Suator", "Suru-suru", "Kolf Braza", "Unir Sirau", "Joerat", 
  "Aswi", "Ayip", "Der Koumor", "Kopay", "Safan", "Pulau Tiga", 
  "Sor Ep", "Jetsy", "Tomor Birip", "Sirets", "Betcbamu", 
  "Joutu", "Awyu", "Koroway Buluanop"
];

// Status Kepegawaian options
const STATUS_KEPEGAWAIAN_OPTIONS = [
  'ASN',
  'PPPK',
  'Pegawai Kontrak',
  'Pegawai Honor Daerah',
  'Non-ASN'
];

// Jabatan Instansi options
const JABATAN_INSTANSI_OPTIONS = [
  'Kepala Dinas',
  'Kepala Kantor',
  'Kepala Badan',
  'Kepala Bagian',
  'Kepala Bidang',
  'Kepala Seksi',
  'Kepala Sub Bagian',
  'Staf'
];

const InstansiPage = () => {
  const { isAdminDinas } = useAuth();
  const navigate = useNavigate();
  const [instansiList, setInstansiList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingInstansi, setEditingInstansi] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterJenis, setFilterJenis] = useState('all');
  const [filterDistrik, setFilterDistrik] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Detail dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedInstansi, setSelectedInstansi] = useState(null);
  const [instansiDetail, setInstansiDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingInstansi, setDeletingInstansi] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Add Pegawai dialog state
  const [showAddPegawaiDialog, setShowAddPegawaiDialog] = useState(false);
  const [addPegawaiInstansi, setAddPegawaiInstansi] = useState(null);
  const [submittingPegawai, setSubmittingPegawai] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pegawaiFormData, setPegawaiFormData] = useState({
    nip: '',
    nama: '',
    email: '',
    password: '',
    role: 'pegawai_instansi',
    status_kepegawaian: '',
    jabatan_instansi: '',
    nama_bidang: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    nama: '',
    alamat: '',
    jenis: 'Kantor Dinas',
    latitude: '',
    longitude: '',
    radius_geofence: 100,
    jam_masuk: '07:30',
    jam_pulang: '16:00',
    toleransi_terlambat: 15,
    kode_instansi: '',
    kampung: '',
    distrik: '',
    telepon: ''
  });

  useEffect(() => {
    loadInstansi();
  }, []);

  const loadInstansi = async () => {
    try {
      setLoading(true);
      const data = await getInstansiList();
      setInstansiList(data);
    } catch (error) {
      console.error('Failed to load instansi:', error);
      toast.error('Gagal memuat data instansi');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius_geofence: parseInt(formData.radius_geofence),
        toleransi_terlambat: parseInt(formData.toleransi_terlambat)
      };

      if (editingInstansi) {
        await updateInstansi(editingInstansi.id, data);
        toast.success('Instansi berhasil diperbarui');
      } else {
        await createInstansi(data);
        toast.success('Instansi berhasil ditambahkan');
      }

      setShowDialog(false);
      resetForm();
      loadInstansi();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data instansi');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (instansi, e) => {
    e.stopPropagation();
    setEditingInstansi(instansi);
    setFormData({
      nama: instansi.nama,
      alamat: instansi.alamat,
      jenis: instansi.jenis || 'Kantor Dinas',
      latitude: instansi.latitude,
      longitude: instansi.longitude,
      radius_geofence: instansi.radius_geofence,
      jam_masuk: instansi.jam_masuk,
      jam_pulang: instansi.jam_pulang,
      toleransi_terlambat: instansi.toleransi_terlambat,
      kode_instansi: instansi.kode_instansi || '',
      kampung: instansi.kampung || '',
      distrik: instansi.distrik || '',
      telepon: instansi.telepon || ''
    });
    setShowDialog(true);
  };

  const openDetailDialog = async (instansi) => {
    setSelectedInstansi(instansi);
    setShowDetailDialog(true);
    setLoadingDetail(true);
    
    try {
      const detail = await getInstansiDetail(instansi.id);
      setInstansiDetail(detail);
    } catch (error) {
      console.error('Failed to load detail:', error);
      toast.error('Gagal memuat detail instansi');
    } finally {
      setLoadingDetail(false);
    }
  };

  const openDeleteDialog = (instansi, e) => {
    e.stopPropagation();
    setDeletingInstansi(instansi);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingInstansi) return;
    setDeleting(true);
    
    try {
      await deleteInstansi(deletingInstansi.id);
      toast.success('Instansi berhasil dihapus');
      setShowDeleteDialog(false);
      setDeletingInstansi(null);
      loadInstansi();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus instansi');
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setEditingInstansi(null);
    setFormData({
      nama: '',
      alamat: '',
      jenis: 'Kantor Dinas',
      latitude: '',
      longitude: '',
      radius_geofence: 100,
      jam_masuk: '07:30',
      jam_pulang: '16:00',
      toleransi_terlambat: 15,
      kode_instansi: '',
      kampung: '',
      distrik: '',
      telepon: ''
    });
  };

  const getCurrentLocation = async () => {
    try {
      const { getCurrentPosition } = await import('../../lib/platformUtils');
      const position = await getCurrentPosition();
      setFormData(prev => ({
        ...prev,
        latitude: position.coords.latitude.toFixed(6),
        longitude: position.coords.longitude.toFixed(6)
      }));
      toast.success('Lokasi berhasil didapatkan');
    } catch {
      toast.error('Gagal mendapatkan lokasi');
    }
  };

  // Filter instansi
  const filteredInstansi = instansiList.filter(instansi => {
    const matchJenis = filterJenis === 'all' || instansi.jenis === filterJenis;
    const matchDistrik = filterDistrik === 'all' || instansi.distrik === filterDistrik;
    const matchSearch = searchQuery === '' || 
      (instansi.nama && instansi.nama.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (instansi.kampung && instansi.kampung.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchJenis && matchDistrik && matchSearch;
  });

  // Count by jenis
  const jenisCounts = JENIS_INSTANSI_OPTIONS.reduce((acc, jenis) => {
    acc[jenis] = instansiList.filter(i => i.jenis === jenis).length;
    return acc;
  }, {});

  const getJenisConfig = (jenis) => {
    return JENIS_INSTANSI_CONFIG[jenis] || JENIS_INSTANSI_CONFIG['Kantor Dinas'];
  };

  // Pegawai Form Handlers
  const handlePegawaiInputChange = (e) => {
    const { name, value } = e.target;
    setPegawaiFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePegawaiSelectChange = (name, value) => {
    setPegawaiFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetPegawaiForm = () => {
    setPegawaiFormData({
      nip: '',
      nama: '',
      email: '',
      password: '',
      role: 'pegawai_instansi',
      status_kepegawaian: '',
      jabatan_instansi: '',
      nama_bidang: ''
    });
    setShowPassword(false);
  };

  const openAddPegawaiDialog = (instansi, e) => {
    if (e) e.stopPropagation();
    setAddPegawaiInstansi(instansi);
    resetPegawaiForm();
    setShowAddPegawaiDialog(true);
  };

  const handleAddPegawaiSubmit = async (e) => {
    e.preventDefault();
    
    if (!addPegawaiInstansi) {
      toast.error('Pilih instansi terlebih dahulu');
      return;
    }

    // Validate NIP
    if (!/^\d{8,}$/.test(pegawaiFormData.nip)) {
      toast.error('NIP harus berupa angka minimal 8 digit');
      return;
    }

    // Validate password
    if (pegawaiFormData.password.length < 8) {
      toast.error('Password minimal 8 karakter');
      return;
    }
    if (!/[A-Z]/.test(pegawaiFormData.password)) {
      toast.error('Password harus mengandung huruf besar');
      return;
    }
    if (!/[a-z]/.test(pegawaiFormData.password)) {
      toast.error('Password harus mengandung huruf kecil');
      return;
    }
    if (!/[0-9]/.test(pegawaiFormData.password)) {
      toast.error('Password harus mengandung angka');
      return;
    }

    setSubmittingPegawai(true);

    try {
      const userData = {
        nip: pegawaiFormData.nip,
        nama: pegawaiFormData.nama,
        email: pegawaiFormData.email || null,
        password: pegawaiFormData.password,
        role: pegawaiFormData.role,
        instansi_id: addPegawaiInstansi.id,
        status_kepegawaian: pegawaiFormData.status_kepegawaian || null,
        jabatan_instansi: pegawaiFormData.jabatan_instansi || null,
        nama_bidang: pegawaiFormData.nama_bidang || null
      };

      await createUser(userData);
      toast.success(`Pegawai berhasil ditambahkan ke ${addPegawaiInstansi.nama}`);
      
      setShowAddPegawaiDialog(false);
      resetPegawaiForm();
      
      // Refresh detail if dialog is open
      if (showDetailDialog && selectedInstansi?.id === addPegawaiInstansi.id) {
        const detail = await getInstansiDetail(addPegawaiInstansi.id);
        setInstansiDetail(detail);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan pegawai');
    } finally {
      setSubmittingPegawai(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="instansi-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-indigo-950">Kelola Instansi</h1>
            <p className="text-gray-500">Manajemen data instansi/kantor dan geofencing</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadInstansi} className="border-indigo-200">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {isAdminDinas && (
            <Dialog open={showDialog} onOpenChange={(open) => {
              setShowDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-950 hover:bg-indigo-900" data-testid="add-instansi-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Instansi
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingInstansi ? 'Edit Instansi' : 'Tambah Instansi Baru'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nama">Nama Instansi</Label>
                    <Input
                      id="nama"
                      name="nama"
                      value={formData.nama}
                      onChange={handleInputChange}
                      placeholder="Dinas Pendidikan Kabupaten..."
                      required
                      data-testid="instansi-nama-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="jenis">Jenis Instansi</Label>
                    <Select 
                      value={formData.jenis} 
                      onValueChange={(value) => handleSelectChange('jenis', value)}
                    >
                      <SelectTrigger data-testid="jenis-select">
                        <SelectValue placeholder="Pilih Jenis" />
                      </SelectTrigger>
                      <SelectContent>
                        {JENIS_INSTANSI_OPTIONS.map(jenis => (
                          <SelectItem key={jenis} value={jenis}>
                            <div className="flex items-center gap-2">
                              <Landmark className="w-4 h-4" />
                              {jenis}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="kode_instansi">Kode Instansi (Opsional)</Label>
                    <Input
                      id="kode_instansi"
                      name="kode_instansi"
                      value={formData.kode_instansi}
                      onChange={handleInputChange}
                      placeholder="Kode unik instansi"
                      data-testid="kode-instansi-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="distrik">Distrik</Label>
                      <Select 
                        value={formData.distrik} 
                        onValueChange={(value) => handleSelectChange('distrik', value)}
                      >
                        <SelectTrigger data-testid="distrik-select">
                          <SelectValue placeholder="Pilih Distrik" />
                        </SelectTrigger>
                        <SelectContent>
                          {DISTRIK_OPTIONS.map(distrik => (
                            <SelectItem key={distrik} value={distrik}>
                              {distrik}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="kampung">Kampung</Label>
                      <Input
                        id="kampung"
                        name="kampung"
                        value={formData.kampung}
                        onChange={handleInputChange}
                        placeholder="Nama Kampung"
                        data-testid="kampung-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="alamat">Alamat</Label>
                    <Input
                      id="alamat"
                      name="alamat"
                      value={formData.alamat}
                      onChange={handleInputChange}
                      placeholder="Jl. Pemerintahan No. 1"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telepon">Telepon (Opsional)</Label>
                    <Input
                      id="telepon"
                      name="telepon"
                      value={formData.telepon}
                      onChange={handleInputChange}
                      placeholder="0901-xxxx"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="latitude">Latitude</Label>
                      <Input
                        id="latitude"
                        name="latitude"
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        placeholder="-5.12345"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="longitude">Longitude</Label>
                      <Input
                        id="longitude"
                        name="longitude"
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        placeholder="138.12345"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={getCurrentLocation}
                    className="w-full border-indigo-200"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Gunakan Lokasi Saat Ini
                  </Button>

                  <div className="space-y-2">
                    <Label htmlFor="radius_geofence">Radius Geofence (meter)</Label>
                    <Input
                      id="radius_geofence"
                      name="radius_geofence"
                      type="number"
                      value={formData.radius_geofence}
                      onChange={handleInputChange}
                      placeholder="100"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="jam_masuk">Jam Masuk</Label>
                      <Input
                        id="jam_masuk"
                        name="jam_masuk"
                        type="time"
                        value={formData.jam_masuk}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jam_pulang">Jam Pulang</Label>
                      <Input
                        id="jam_pulang"
                        name="jam_pulang"
                        type="time"
                        value={formData.jam_pulang}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="toleransi_terlambat">Toleransi Terlambat (menit)</Label>
                    <Input
                      id="toleransi_terlambat"
                      name="toleransi_terlambat"
                      type="number"
                      value={formData.toleransi_terlambat}
                      onChange={handleInputChange}
                      placeholder="15"
                      required
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowDialog(false);
                        resetForm();
                      }}
                      className="flex-1"
                    >
                      Batal
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-indigo-950"
                      data-testid="submit-instansi-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        editingInstansi ? 'Simpan Perubahan' : 'Tambah Instansi'
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className="mb-6 space-y-4">
        {/* Distrik & Search */}
        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Filter Distrik</Label>
                <Select value={filterDistrik} onValueChange={setFilterDistrik}>
                  <SelectTrigger data-testid="filter-distrik-select">
                    <SelectValue placeholder="Semua Distrik" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Distrik</SelectItem>
                    {DISTRIK_OPTIONS.map(distrik => (
                      <SelectItem key={distrik} value={distrik}>{distrik}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Cari Instansi / Kampung</Label>
                <Input
                  placeholder="Ketik nama instansi atau kampung..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilterDistrik('all');
                    setSearchQuery('');
                    setFilterJenis('all');
                  }}
                  className="w-full border-indigo-200"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Filter
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Jenis Filter */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filterJenis === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterJenis('all')}
            className={filterJenis === 'all' ? 'bg-indigo-950' : 'border-indigo-200'}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Semua ({instansiList.length})
          </Button>
          {JENIS_INSTANSI_OPTIONS.map(jenis => {
            const count = jenisCounts[jenis] || 0;
            return (
              <Button
                key={jenis}
                variant={filterJenis === jenis ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterJenis(jenis)}
                className={filterJenis === jenis ? 'bg-indigo-950' : `border-indigo-200 ${count === 0 ? 'opacity-50' : ''}`}
                disabled={count === 0}
              >
                {jenis} ({count})
              </Button>
            );
          })}
        </div>

        {/* Active Filters Summary */}
        {(filterDistrik !== 'all' || searchQuery || filterJenis !== 'all') && (
          <div className="mt-3 text-sm text-gray-600">
            Menampilkan <span className="font-semibold text-indigo-700">{filteredInstansi.length}</span> dari {instansiList.length} instansi
            {filterDistrik !== 'all' && <span> di <span className="font-medium">Distrik {filterDistrik}</span></span>}
            {searchQuery && <span> yang cocok dengan "<span className="font-medium">{searchQuery}</span>"</span>}
          </div>
        )}
      </div>

      {/* Instansi List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredInstansi.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Landmark className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">
            {filterJenis === 'all' && filterDistrik === 'all' && !searchQuery 
              ? 'Belum ada data instansi' 
              : 'Tidak ada instansi yang cocok dengan filter'}
          </p>
          <p className="text-sm">
            {filterJenis === 'all' && filterDistrik === 'all' && !searchQuery 
              ? 'Klik tombol "Tambah Instansi" untuk menambahkan' 
              : 'Ubah filter atau coba kata kunci lain'}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInstansi.map((instansi) => {
            const config = getJenisConfig(instansi.jenis);
            return (
              <Card 
                key={instansi.id} 
                className={`border-indigo-100/50 card-hover border-l-4 ${config.cardBorder} cursor-pointer transition-all hover:shadow-lg`}
                onClick={() => openDetailDialog(instansi)}
              >
                <CardHeader className={`pb-2 bg-gradient-to-r ${config.bgGradient} rounded-tr-lg`}>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color.replace('text-', 'bg-').split(' ')[0]}`}>
                        <Landmark className={`w-5 h-5 ${config.color.split(' ')[1]}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-lg font-semibold truncate block text-indigo-950 hover:text-indigo-700">
                          {instansi.nama}
                        </span>
                        <Badge className={`${config.color} text-xs`}>
                          {instansi.jenis}
                        </Badge>
                      </div>
                    </div>
                    {isAdminDinas && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => openAddPegawaiDialog(instansi, e)}
                          title="Tambah Pegawai"
                          data-testid={`add-pegawai-${instansi.id}`}
                        >
                          <UserPlus className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => openEditDialog(instansi, e)}
                          data-testid={`edit-instansi-${instansi.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => openDeleteDialog(instansi, e)}
                          data-testid={`delete-instansi-${instansi.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3">
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{instansi.alamat}</p>
                  
                  <div className="space-y-2 text-sm">
                    {instansi.kode_instansi && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-medium">Kode:</span>
                        <span className="font-mono">{instansi.kode_instansi}</span>
                      </div>
                    )}
                    {instansi.distrik && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-medium">Distrik:</span>
                        <span>{instansi.distrik}</span>
                        {instansi.kampung && <span className="text-gray-400">• {instansi.kampung}</span>}
                      </div>
                    )}
                    {instansi.telepon && (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{instansi.telepon}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-gray-500">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span className="font-mono text-xs truncate">
                        {instansi.latitude.toFixed(6)}, {instansi.longitude.toFixed(6)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-500">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      <span>{instansi.jam_masuk} - {instansi.jam_pulang}</span>
                    </div>
                    
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <Badge variant="outline" className="border-indigo-200">
                        Radius {instansi.radius_geofence}m
                      </Badge>
                      <Badge variant="outline" className="border-amber-200">
                        Toleransi {instansi.toleransi_terlambat} menit
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedInstansi && (
                <>
                  {(() => {
                    const config = getJenisConfig(selectedInstansi.jenis);
                    return (
                      <>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color.replace('text-', 'bg-').split(' ')[0]}`}>
                          <Landmark className={`w-5 h-5 ${config.color.split(' ')[1]}`} />
                        </div>
                        <div>
                          <span className="text-xl">{selectedInstansi.nama}</span>
                          <Badge className={`${config.color} ml-2`}>
                            {selectedInstansi.jenis}
                          </Badge>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : instansiDetail ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-indigo-100/50">
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
                    <p className="text-2xl font-bold text-indigo-950">{instansiDetail.summary.total_pegawai}</p>
                    <p className="text-sm text-gray-500">Total Pegawai</p>
                  </CardContent>
                </Card>
                <Card className="border-emerald-100/50">
                  <CardContent className="p-4 text-center">
                    <UserCheck className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
                    <p className="text-2xl font-bold text-emerald-600">{instansiDetail.summary.sudah_absen_hari_ini}</p>
                    <p className="text-sm text-gray-500">Sudah Absen</p>
                  </CardContent>
                </Card>
                <Card className="border-red-100/50">
                  <CardContent className="p-4 text-center">
                    <UserX className="w-8 h-8 mx-auto mb-2 text-red-600" />
                    <p className="text-2xl font-bold text-red-600">{instansiDetail.summary.belum_absen_hari_ini}</p>
                    <p className="text-sm text-gray-500">Belum Absen</p>
                  </CardContent>
                </Card>
              </div>

              {/* Pegawai Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg text-indigo-950">Daftar Pegawai & Absensi</h3>
                  {isAdminDinas && (
                    <Button
                      size="sm"
                      onClick={() => openAddPegawaiDialog(selectedInstansi)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="add-pegawai-from-detail-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Tambah Pegawai
                    </Button>
                  )}
                </div>
                {instansiDetail.pegawai_list.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p className="mb-2">Belum ada pegawai terdaftar di instansi ini</p>
                    {isAdminDinas && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAddPegawaiDialog(selectedInstansi)}
                        className="border-emerald-300 text-emerald-700"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Tambah Pegawai Pertama
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>NIP</th>
                          <th>Nama Pegawai</th>
                          <th>Jabatan / Bidang</th>
                          <th>Status Hari Ini</th>
                          <th>Rekap Bulan Ini</th>
                        </tr>
                      </thead>
                      <tbody>
                        {instansiDetail.pegawai_list.map(pegawai => (
                          <tr key={pegawai.id}>
                            <td className="font-mono text-sm">{pegawai.nip}</td>
                            <td className="font-medium">{pegawai.nama}</td>
                            <td>
                              <div className="flex flex-col gap-1">
                                {pegawai.jabatan_instansi && (
                                  <Badge className="bg-indigo-100 text-indigo-800 text-xs w-fit">
                                    {pegawai.jabatan_instansi}
                                  </Badge>
                                )}
                                {pegawai.nama_bidang && (
                                  <span className="text-xs text-gray-600">{pegawai.nama_bidang}</span>
                                )}
                                {!pegawai.jabatan_instansi && !pegawai.nama_bidang && (
                                  <span className="text-gray-400">-</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="flex gap-2">
                                <Badge className={pegawai.sudah_absen_masuk 
                                  ? 'bg-emerald-100 text-emerald-800' 
                                  : 'bg-gray-100 text-gray-600'}>
                                  Masuk: {pegawai.sudah_absen_masuk ? '✓' : '-'}
                                </Badge>
                                <Badge className={pegawai.sudah_absen_pulang 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-gray-100 text-gray-600'}>
                                  Pulang: {pegawai.sudah_absen_pulang ? '✓' : '-'}
                                </Badge>
                              </div>
                            </td>
                            <td>
                              <div className="flex gap-1 text-xs">
                                <Badge className="bg-emerald-100 text-emerald-800">
                                  {pegawai.rekap_bulan_ini.hadir_tepat_waktu} Tepat
                                </Badge>
                                <Badge className="bg-amber-100 text-amber-800">
                                  {pegawai.rekap_bulan_ini.hadir_terlambat} Terlambat
                                </Badge>
                                <Badge className="bg-red-100 text-red-800">
                                  {pegawai.rekap_bulan_ini.mangkir} Mangkir
                                </Badge>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Instansi Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-indigo-950">Informasi Instansi</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {instansiDetail.instansi.kode_instansi && (
                    <div>
                      <span className="text-gray-500">Kode Instansi:</span>
                      <p className="font-mono font-medium">{instansiDetail.instansi.kode_instansi}</p>
                    </div>
                  )}
                  {instansiDetail.instansi.distrik && (
                    <div>
                      <span className="text-gray-500">Distrik:</span>
                      <p className="font-medium">{instansiDetail.instansi.distrik}</p>
                    </div>
                  )}
                  {instansiDetail.instansi.kampung && (
                    <div>
                      <span className="text-gray-500">Kampung:</span>
                      <p className="font-medium">{instansiDetail.instansi.kampung}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Alamat:</span>
                    <p className="font-medium">{instansiDetail.instansi.alamat}</p>
                  </div>
                  {instansiDetail.instansi.telepon && (
                    <div>
                      <span className="text-gray-500">Telepon:</span>
                      <p className="font-medium">{instansiDetail.instansi.telepon}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Koordinat:</span>
                    <p className="font-mono text-xs">
                      {instansiDetail.instansi.latitude.toFixed(6)}, {instansiDetail.instansi.longitude.toFixed(6)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Jam Operasional:</span>
                    <p className="font-medium">{instansiDetail.instansi.jam_masuk} - {instansiDetail.instansi.jam_pulang}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Radius Geofence:</span>
                    <p className="font-medium">{instansiDetail.instansi.radius_geofence} meter</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Hapus Instansi
            </DialogTitle>
            <DialogDescription>
              Apakah Anda yakin ingin menghapus instansi <strong>{deletingInstansi?.nama}</strong>?
              Tindakan ini tidak dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              Pastikan tidak ada pegawai yang terdaftar di instansi ini sebelum menghapus.
            </AlertDescription>
          </Alert>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeletingInstansi(null);
              }}
            >
              Batal
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Hapus Instansi
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Pegawai Dialog */}
      <Dialog open={showAddPegawaiDialog} onOpenChange={(open) => {
        setShowAddPegawaiDialog(open);
        if (!open) resetPegawaiForm();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Tambah Pegawai
            </DialogTitle>
          </DialogHeader>
          
          {addPegawaiInstansi && (
            <div className="bg-indigo-50 p-3 rounded-lg mb-4">
              <p className="text-sm text-indigo-800">
                <strong>Instansi:</strong> {addPegawaiInstansi.nama}
              </p>
              <p className="text-xs text-indigo-600">
                {addPegawaiInstansi.jenis} • {addPegawaiInstansi.alamat}
              </p>
            </div>
          )}

          <form onSubmit={handleAddPegawaiSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pegawai-nip">NIP</Label>
              <Input
                id="pegawai-nip"
                name="nip"
                value={pegawaiFormData.nip}
                onChange={handlePegawaiInputChange}
                placeholder="198501012010011001"
                required
                data-testid="pegawai-nip-input"
              />
              <p className="text-xs text-gray-500">Minimal 8 digit angka, digunakan untuk login</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pegawai-nama">Nama Lengkap</Label>
              <Input
                id="pegawai-nama"
                name="nama"
                value={pegawaiFormData.nama}
                onChange={handlePegawaiInputChange}
                placeholder="Budi Santoso, S.E"
                required
                data-testid="pegawai-nama-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pegawai-email">Email (Opsional)</Label>
              <Input
                id="pegawai-email"
                name="email"
                type="email"
                value={pegawaiFormData.email}
                onChange={handlePegawaiInputChange}
                placeholder="budi@example.com"
                data-testid="pegawai-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pegawai-status-kepegawaian">Status Kepegawaian</Label>
              <Select 
                value={pegawaiFormData.status_kepegawaian} 
                onValueChange={(value) => handlePegawaiSelectChange('status_kepegawaian', value)}
              >
                <SelectTrigger data-testid="pegawai-status-kepegawaian-select">
                  <SelectValue placeholder="Pilih Status Kepegawaian" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_KEPEGAWAIAN_OPTIONS.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pegawai-jabatan">Jabatan</Label>
              <Select 
                value={pegawaiFormData.jabatan_instansi} 
                onValueChange={(value) => handlePegawaiSelectChange('jabatan_instansi', value)}
              >
                <SelectTrigger data-testid="pegawai-jabatan-select">
                  <SelectValue placeholder="Pilih Jabatan" />
                </SelectTrigger>
                <SelectContent>
                  {JABATAN_INSTANSI_OPTIONS.map(jabatan => (
                    <SelectItem key={jabatan} value={jabatan}>{jabatan}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pegawai-nama-bidang">Nama Bidang/Bagian/Sub Bagian</Label>
              <Input
                id="pegawai-nama-bidang"
                name="nama_bidang"
                value={pegawaiFormData.nama_bidang}
                onChange={handlePegawaiInputChange}
                placeholder="Contoh: Bidang Pendidikan Dasar"
                data-testid="pegawai-nama-bidang-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pegawai-password">Password</Label>
              <div className="relative">
                <Input
                  id="pegawai-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={pegawaiFormData.password}
                  onChange={handlePegawaiInputChange}
                  placeholder="Min. 8 karakter"
                  required
                  className="pr-10"
                  data-testid="pegawai-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500">Harus mengandung huruf besar, huruf kecil, dan angka</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddPegawaiDialog(false);
                  resetPegawaiForm();
                }}
                className="flex-1"
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={submittingPegawai}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="submit-pegawai-btn"
              >
                {submittingPegawai ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Tambah Pegawai
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InstansiPage;
